import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Users,
  Search,
  Loader2,
  Crown,
  Shield,
  Calendar,
  Mail,
  Activity,
  Download,
  FileDown,
  UserPlus,
  X,
  ChevronUp,
  ChevronDown,
  Filter,
  Ban,
  Check,
  StickyNote,
  Eye,
  Globe,
  Flame,
  Target,
  BookOpen,
  Clock,
  Zap,
  ArrowUpDown,
  CheckSquare,
  Square,
  Trash2,
  RefreshCcw,
  BarChart3,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface UserRow {
  id: string;
  email: string;
  // Typed columns (source of truth post DB-01 migration)
  name?: string | null;
  display_name?: string | null;
  xp?: number | null;
  level?: number | null;
  streak?: number | null;
  is_premium?: boolean | null;
  preferred_language?: string | null;
  last_login_date?: string | null;
  is_dark_mode?: boolean | null;
  share_data_with_ai?: boolean | null;
  consent_given?: boolean | null;
  has_seen_onboarding?: boolean | null;
  simulation_attempts?: number | null;
  daily_goal?: number | null;
  exam_date?: string | null;
  data: {
    name?: string;
    displayName?: string;
    xp?: number;
    level?: number;
    streak?: number;
    isPremium?: boolean;
    isTestUser?: boolean;
    isBlocked?: boolean;
    adminNote?: string;
    language?: string;
    completedQuestions?: string[];
    correctlyAnswered?: string[];
    failedQuestions?: string[];
    bookmarkedQuestions?: string[];
    chapterProgress?: Record<string, number>;
    lastLoginDate?: string;
    isDarkMode?: boolean;
    shareDataWithAI?: boolean;
    consentGiven?: boolean;
    hasSeenOnboarding?: boolean;
    badges?: any[];
    simulationAttempts?: number;
    dailyGoal?: number;
    dailyProgress?: { date: string; questionsAnswered: number; xpEarned: number };
    examDate?: string;
    srsData?: Record<string, any>;
    essencesRead?: number[];
  } | null;
  created_at: string;
  updated_at: string;
}

// Resolves the best display name, preferring typed columns over legacy JSONB.
const resolveName = (u: UserRow): string =>
  u.display_name || u.name || u.data?.displayName || u.data?.name || 'Unbekannt';

type SortKey = 'name' | 'email' | 'level' | 'xp' | 'streak' | 'updated_at' | 'created_at';
type SortDir = 'asc' | 'desc';

const LANGUAGE_MAP: Record<string, string> = {
  de: '🇩🇪 Deutsch',
  tr: '🇹🇷 Türkisch',
  ar: '🇸🇦 Arabisch',
  en: '🇬🇧 Englisch',
};

function relativeTime(dateStr: string | undefined): {
  text: string;
  color: string;
  tooltip: string;
} {
  if (!dateStr) return { text: '—', color: 'var(--text-muted)', tooltip: '' };
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const dateFormatted = d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const fullDate = d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  if (mins < 5) return { text: 'Gerade eben', color: '#16a34a', tooltip: fullDate };
  if (mins < 60) return { text: `vor ${mins} Min.`, color: '#16a34a', tooltip: fullDate };
  if (hours < 24)
    return { text: `vor ${hours} Std. (${dateFormatted})`, color: '#16a34a', tooltip: fullDate };
  if (days < 3)
    return {
      text: `vor ${days} Tag${days > 1 ? 'en' : ''} (${dateFormatted})`,
      color: '#ca8a04',
      tooltip: fullDate,
    };
  if (days < 7) return { text: `${dateFormatted}`, color: '#ea580c', tooltip: fullDate };
  if (days < 30) return { text: `${dateFormatted}`, color: '#dc2626', tooltip: fullDate };
  return {
    text: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    color: '#dc2626',
    tooltip: fullDate,
  };
}

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPremium, setFilterPremium] = useState<'' | 'yes' | 'no'>('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterActivity, setFilterActivity] = useState<'' | 'active' | 'inactive' | 'blocked'>('');
  const [filterTestUser, setFilterTestUser] = useState<'' | 'yes' | 'no'>('');
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ text: string; type: string } | null>(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('updated_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('users-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setUsers((prev) => [payload.new as UserRow, ...prev]);
          setMessage({
            text: `Neuer Nutzer registriert: ${(payload.new as UserRow).email}`,
            type: 'success',
          });
        } else if (payload.eventType === 'UPDATE') {
          setUsers((prev) =>
            prev.map((u) => (u.id === (payload.new as UserRow).id ? (payload.new as UserRow) : u))
          );
        } else if (payload.eventType === 'DELETE') {
          setUsers((prev) => prev.filter((u) => u.id !== (payload.old as any).id));
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await adminDb
        .from('users')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'updated_at' || key === 'created_at' ? 'desc' : 'asc');
    }
  };

  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const filtered = useMemo(() => {
    let result = [...users];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (u) => u.email.toLowerCase().includes(term) || resolveName(u).toLowerCase().includes(term)
      );
    }
    if (filterPremium === 'yes') result = result.filter((u) => u.data?.isPremium);
    if (filterPremium === 'no') result = result.filter((u) => !u.data?.isPremium);
    if (filterLanguage)
      result = result.filter((u) => (u.data?.language || 'de') === filterLanguage);
    if (filterTestUser === 'yes') result = result.filter((u) => u.data?.isTestUser);
    if (filterTestUser === 'no') result = result.filter((u) => !u.data?.isTestUser);
    if (filterActivity === 'active') {
      result = result.filter((u) => {
        const d = u.data?.lastLoginDate || u.updated_at;
        return d && Date.now() - new Date(d).getTime() < 7 * 86400000;
      });
    }
    if (filterActivity === 'inactive') {
      result = result.filter((u) => {
        const d = u.data?.lastLoginDate || u.updated_at;
        return !d || Date.now() - new Date(d).getTime() >= 7 * 86400000;
      });
    }
    if (filterActivity === 'blocked') result = result.filter((u) => u.data?.isBlocked);

    // Sort
    result.sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortKey) {
        case 'name':
          aVal = resolveName(a).toLowerCase();
          bVal = resolveName(b).toLowerCase();
          break;
        case 'email':
          aVal = a.email;
          bVal = b.email;
          break;
        case 'level':
          aVal = a.level ?? a.data?.level ?? 0;
          bVal = b.level ?? b.data?.level ?? 0;
          break;
        case 'xp':
          aVal = a.xp ?? a.data?.xp ?? 0;
          bVal = b.xp ?? b.data?.xp ?? 0;
          break;
        case 'streak':
          aVal = a.streak ?? a.data?.streak ?? 0;
          bVal = b.streak ?? b.data?.streak ?? 0;
          break;
        case 'updated_at':
          aVal = new Date(a.updated_at).getTime();
          bVal = new Date(b.updated_at).getTime();
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [
    users,
    searchTerm,
    filterPremium,
    filterLanguage,
    filterActivity,
    filterTestUser,
    sortKey,
    sortDir,
  ]);

  const premiumCount = users.filter((u) => u.data?.isPremium).length;
  const activeCount = users.filter((u) => {
    const d = u.data?.lastLoginDate || u.updated_at;
    return d && Date.now() - new Date(d).getTime() < 7 * 86400000;
  }).length;
  const blockedCount = users.filter((u) => u.data?.isBlocked).length;

  const togglePremium = async (user: UserRow) => {
    const newPremium = !user.data?.isPremium;
    try {
      const updatedData = { ...user.data, isPremium: newPremium };
      const { error } = await adminDb.from('users').update({ data: updatedData }).eq('id', user.id);
      if (error) throw error;
      setMessage({
        text: `Premium ${newPremium ? 'aktiviert' : 'deaktiviert'} für ${user.email}`,
        type: 'success',
      });
      await loadUsers();
      if (selectedUser?.id === user.id) setSelectedUser({ ...user, data: updatedData });
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    }
  };

  const toggleBlock = async (user: UserRow) => {
    const newBlocked = !user.data?.isBlocked;
    try {
      const updatedData = { ...user.data, isBlocked: newBlocked };
      const { error } = await adminDb.from('users').update({ data: updatedData }).eq('id', user.id);
      if (error) throw error;
      setMessage({
        text: `Nutzer ${newBlocked ? 'gesperrt' : 'entsperrt'}: ${user.email}`,
        type: 'success',
      });
      await loadUsers();
      if (selectedUser?.id === user.id) setSelectedUser({ ...user, data: updatedData });
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    }
  };

  const saveNote = async () => {
    if (!selectedUser) return;
    setSavingNote(true);
    try {
      const updatedData = { ...selectedUser.data, adminNote: noteText };
      const { error } = await adminDb
        .from('users')
        .update({ data: updatedData })
        .eq('id', selectedUser.id);
      if (error) throw error;
      setSelectedUser({ ...selectedUser, data: updatedData });
      setMessage({ text: 'Notiz gespeichert', type: 'success' });
      await loadUsers();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setSavingNote(false);
    }
  };

  // Bulk actions
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((u) => u.id)));
    }
  };
  const bulkTogglePremium = async (premium: boolean) => {
    for (const id of selectedIds) {
      const user = users.find((u) => u.id === id);
      if (user) {
        const updatedData = { ...user.data, isPremium: premium };
        await adminDb.from('users').update({ data: updatedData }).eq('id', id);
      }
    }
    setMessage({
      text: `Premium ${premium ? 'aktiviert' : 'deaktiviert'} für ${selectedIds.size} Nutzer`,
      type: 'success',
    });
    setSelectedIds(new Set());
    await loadUsers();
  };
  const bulkDelete = async () => {
    if (
      !confirm(
        `${selectedIds.size} Nutzer wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
      )
    )
      return;
    let deleted = 0;
    let errors: string[] = [];
    for (const id of selectedIds) {
      const user = users.find((u) => u.id === id);
      const { error } = await adminDb.from('users').delete().eq('id', id);
      if (error) {
        errors.push(`${user?.email || id}: ${error.message}`);
      } else {
        deleted++;
        // Audit log
        try {
          await adminDb.from('audit_logs').insert({
            action: 'user_deleted',
            resource_type: 'users',
            admin_email: 'admin',
            resource_id: id,
            old_value: { email: user?.email, name: user?.data?.name },
          });
        } catch {
          /* ignore audit errors */
        }
      }
    }
    if (errors.length > 0) {
      setMessage({
        text: `${deleted} gelöscht, ${errors.length} Fehler: ${errors[0]}`,
        type: 'error',
      });
    } else {
      setMessage({ text: `${deleted} Nutzer erfolgreich gelöscht`, type: 'success' });
    }
    setSelectedIds(new Set());
    setSelectedUser(null);
    await loadUsers();
  };

  // Quick filter chips
  const applyQuickFilter = (type: string) => {
    setSearchTerm('');
    setFilterPremium('');
    setFilterLanguage('');
    setFilterTestUser('');
    switch (type) {
      case 'inactive7':
        setFilterActivity('inactive');
        break;
      case 'highLevelFree':
        setFilterActivity('');
        setFilterPremium('no');
        setSortKey('level');
        setSortDir('desc');
        break;
      case 'newThisWeek':
        setFilterActivity('');
        setSortKey('created_at');
        setSortDir('desc');
        break;
      case 'blocked':
        setFilterActivity('blocked');
        break;
      default:
        setFilterActivity('');
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <Users size={24} /> Nutzerverwaltung
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <Users size={24} /> Nutzerverwaltung
        </h1>
        <div className="flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreateUser(true)}>
            <UserPlus size={14} /> Nutzer anlegen
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleExportUsersCSV}>
            <Download size={14} /> CSV Export
          </button>
          <button className="btn btn-ghost btn-sm" onClick={loadUsers} title="Aktualisieren">
            <RefreshCcw size={14} />
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
          <span>{message.text}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* KPIs */}
      <div
        className="grid-cols-4 mb-4"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}
      >
        <div className="kpi-card">
          <span className="kpi-label">Nutzer gesamt</span>
          <span className="kpi-value">{users.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Premium</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>
            {premiumCount}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Aktiv (7 Tage)</span>
          <span className="kpi-value" style={{ color: '#16a34a' }}>
            {activeCount}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Gesperrt</span>
          <span
            className="kpi-value"
            style={{ color: blockedCount > 0 ? '#dc2626' : 'var(--text-muted)' }}
          >
            {blockedCount}
          </span>
        </div>
      </div>

      {/* Quick Filter Chips */}
      <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
        <button
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11 }}
          onClick={() => applyQuickFilter('all')}
        >
          🔄 Alle anzeigen
        </button>
        <button
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11, border: '1px solid #dc2626', color: '#dc2626' }}
          onClick={() => applyQuickFilter('inactive7')}
        >
          💤 Inaktiv &gt;7 Tage
        </button>
        <button
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11, border: '1px solid #ca8a04', color: '#ca8a04' }}
          onClick={() => applyQuickFilter('highLevelFree')}
        >
          🎯 Hohes Level, kein Premium
        </button>
        <button
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11, border: '1px solid #16a34a', color: '#16a34a' }}
          onClick={() => applyQuickFilter('newThisWeek')}
        >
          ✨ Neueste zuerst
        </button>
        {blockedCount > 0 && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 11, border: '1px solid #dc2626', color: '#dc2626' }}
            onClick={() => applyQuickFilter('blocked')}
          >
            🚫 Gesperrt ({blockedCount})
          </button>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            background: 'var(--primary)',
            color: '#fff',
            borderRadius: 'var(--radius-lg)',
            padding: '8px 16px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>{selectedIds.size} ausgewählt</span>
          <button
            className="btn btn-sm"
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}
            onClick={() => bulkTogglePremium(true)}
          >
            <Crown size={12} /> Premium vergeben
          </button>
          <button
            className="btn btn-sm"
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}
            onClick={() => bulkTogglePremium(false)}
          >
            Premium entfernen
          </button>
          <button
            className="btn btn-sm"
            style={{ background: 'rgba(220,38,38,0.8)', color: '#fff', border: 'none' }}
            onClick={bulkDelete}
          >
            <Trash2 size={12} /> Löschen
          </button>
          <button
            className="btn btn-sm"
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              marginLeft: 'auto',
            }}
            onClick={() => setSelectedIds(new Set())}
          >
            <X size={12} /> Auswahl aufheben
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20 }}>
        {/* Users Table */}
        <div className="table-container" style={{ flex: 1 }}>
          <div className="table-toolbar">
            <div className="flex gap-3 items-center" style={{ flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', minWidth: 200 }}>
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                  }}
                />
                <input
                  className="input input-search"
                  placeholder="Name oder E-Mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="input"
                style={{ width: 100, fontSize: 12 }}
                value={filterPremium}
                onChange={(e) => setFilterPremium(e.target.value as any)}
              >
                <option value="">Status</option>
                <option value="yes">Premium</option>
                <option value="no">Free</option>
              </select>
              <select
                className="input"
                style={{ width: 100, fontSize: 12 }}
                value={filterLanguage}
                onChange={(e) => setFilterLanguage(e.target.value)}
              >
                <option value="">Sprache</option>
                <option value="de">Deutsch</option>
                <option value="tr">Türkisch</option>
                <option value="ar">Arabisch</option>
                <option value="en">Englisch</option>
              </select>
              <select
                className="input"
                style={{ width: 110, fontSize: 12 }}
                value={filterActivity}
                onChange={(e) => setFilterActivity(e.target.value as any)}
              >
                <option value="">Aktivität</option>
                <option value="active">Aktiv (&lt;7d)</option>
                <option value="inactive">Inaktiv (&gt;7d)</option>
                <option value="blocked">Gesperrt</option>
              </select>
              <select
                className="input"
                style={{ width: 100, fontSize: 12 }}
                value={filterTestUser}
                onChange={(e) => setFilterTestUser(e.target.value as any)}
              >
                <option value="">Test-User</option>
                <option value="yes">Nur Test</option>
                <option value="no">Kein Test</option>
              </select>
            </div>
            <span className="text-xs text-muted">{filtered.length} Nutzer</span>
          </div>

          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <button
                    onClick={selectAll}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                  >
                    {selectedIds.size === filtered.length && filtered.length > 0 ? (
                      <CheckSquare size={14} />
                    ) : (
                      <Square size={14} style={{ opacity: 0.4 }} />
                    )}
                  </button>
                </th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-1">
                    Nutzer <SortIcon col="name" />
                  </div>
                </th>
                <th>Status</th>
                <th onClick={() => handleSort('level')} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-1">
                    Level <SortIcon col="level" />
                  </div>
                </th>
                <th onClick={() => handleSort('xp')} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-1">
                    XP <SortIcon col="xp" />
                  </div>
                </th>
                <th onClick={() => handleSort('streak')} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-1">
                    Streak <SortIcon col="streak" />
                  </div>
                </th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-1">
                    Registriert <SortIcon col="created_at" />
                  </div>
                </th>
                <th onClick={() => handleSort('updated_at')} style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-1">
                    Letzte Aktivität <SortIcon col="updated_at" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => {
                const activity = relativeTime(user.data?.lastLoginDate || user.updated_at);
                return (
                  <tr
                    key={user.id}
                    onClick={() => {
                      setSelectedUser(user);
                      setNoteText(user.data?.adminNote || '');
                    }}
                    style={{ cursor: 'pointer', opacity: user.data?.isBlocked ? 0.5 : 1 }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => toggleSelect(user.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 2,
                        }}
                      >
                        {selectedIds.has(user.id) ? (
                          <CheckSquare size={14} style={{ color: 'var(--primary)' }} />
                        ) : (
                          <Square size={14} style={{ opacity: 0.4 }} />
                        )}
                      </button>
                    </td>
                    <td>
                      <div>
                        <div
                          style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          {resolveName(user)}
                          {user.data?.isTestUser && (
                            <span
                              className="badge"
                              style={{
                                background: '#f0e6ff',
                                color: '#7c3aed',
                                fontSize: 10,
                                padding: '1px 6px',
                              }}
                            >
                              🧪
                            </span>
                          )}
                          {user.data?.isBlocked && (
                            <span
                              className="badge"
                              style={{
                                background: '#fef2f2',
                                color: '#dc2626',
                                fontSize: 10,
                                padding: '1px 6px',
                              }}
                            >
                              🚫
                            </span>
                          )}
                          {user.data?.adminNote && (
                            <StickyNote size={10} style={{ color: '#ca8a04' }} />
                          )}
                        </div>
                        <div className="text-xs text-muted">{user.email}</div>
                      </div>
                    </td>
                    <td>
                      {(user.is_premium ?? user.data?.isPremium) ? (
                        <span className="badge badge-success">
                          <Crown size={10} /> Premium
                        </span>
                      ) : (
                        <span className="badge badge-neutral">Free</span>
                      )}
                    </td>
                    <td>{user.level ?? user.data?.level ?? 0}</td>
                    <td>{(user.xp ?? user.data?.xp ?? 0).toLocaleString()}</td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Flame
                          size={12}
                          style={{
                            color:
                              ((user.streak ?? user.data?.streak) || 0) > 0
                                ? '#ea580c'
                                : 'var(--text-muted)',
                          }}
                        />
                        {user.streak ?? user.data?.streak ?? 0}
                      </span>
                    </td>
                    <td className="text-xs text-muted">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString('de-DE')
                        : '—'}
                    </td>
                    <td title={activity.tooltip}>
                      <span className="text-xs" style={{ color: activity.color, fontWeight: 500 }}>
                        {activity.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                  >
                    Keine Nutzer gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* User Detail Panel */}
        {selectedUser && (
          <div
            className="card"
            style={{
              width: 380,
              flexShrink: 0,
              alignSelf: 'flex-start',
              position: 'sticky',
              top: 0,
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3>Nutzer-Detail</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedUser(null)}>
                <X size={16} />
              </button>
            </div>

            {/* Basic Info */}
            <div className="flex flex-col gap-3">
              <div>
                <span className="text-xs text-muted">Name</span>
                <p style={{ fontWeight: 600 }}>{resolveName(selectedUser)}</p>
              </div>
              <div>
                <span className="text-xs text-muted">E-Mail</span>
                <p className="text-sm">{selectedUser.email}</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <span className="text-xs text-muted">Level</span>
                  <p style={{ fontWeight: 700, fontSize: 20 }}>{selectedUser.data?.level ?? 0}</p>
                </div>
                <div>
                  <span className="text-xs text-muted">XP</span>
                  <p style={{ fontWeight: 700, fontSize: 20 }}>
                    {(selectedUser.data?.xp ?? 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted">Streak</span>
                  <p style={{ fontWeight: 700, fontSize: 20 }}>
                    {selectedUser.data?.streak ?? 0}🔥
                  </p>
                </div>
              </div>

              <hr
                style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' }}
              />

              {/* Customer Data */}
              <div>
                <span
                  className="text-xs text-muted"
                  style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}
                >
                  📋 Kundendaten
                </span>
                <div
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}
                >
                  <div>
                    <span className="text-xs text-muted">Registriert</span>
                    <p style={{ fontWeight: 500 }}>
                      {selectedUser.created_at
                        ? new Date(selectedUser.created_at).toLocaleDateString('de-DE')
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Letzter Login</span>
                    <p
                      style={{
                        fontWeight: 500,
                        color: relativeTime(
                          selectedUser.data?.lastLoginDate || selectedUser.updated_at
                        ).color,
                      }}
                    >
                      {
                        relativeTime(selectedUser.data?.lastLoginDate || selectedUser.updated_at)
                          .text
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Sprache</span>
                    <p>
                      {LANGUAGE_MAP[selectedUser.data?.language || 'de'] ||
                        selectedUser.data?.language}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Status</span>
                    <p>
                      {selectedUser.data?.isPremium ? (
                        <span className="badge badge-success" style={{ fontSize: 10 }}>
                          <Crown size={9} /> Premium
                        </span>
                      ) : (
                        <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                          Free
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Simulationen</span>
                    <p style={{ fontWeight: 500 }}>{selectedUser.data?.simulationAttempts ?? 0}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Prüfungsdatum</span>
                    <p style={{ fontWeight: 500 }}>
                      {selectedUser.data?.examDate
                        ? new Date(selectedUser.data.examDate).toLocaleDateString('de-DE')
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">DSGVO-Einwilligung</span>
                    <p style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {selectedUser.data?.consentGiven === true ? (
                        <>
                          <CheckCircle size={12} style={{ color: '#16a34a' }} /> Ja
                        </>
                      ) : selectedUser.data?.consentGiven === false ? (
                        <>
                          <XCircle size={12} style={{ color: '#dc2626' }} /> Nein
                        </>
                      ) : (
                        <>
                          <Clock size={12} style={{ color: '#ca8a04' }} /> Ausstehend
                        </>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">KI-Daten teilen</span>
                    <p style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {selectedUser.data?.shareDataWithAI ? (
                        <>
                          <CheckCircle size={12} style={{ color: '#16a34a' }} /> Ja
                        </>
                      ) : (
                        <>
                          <XCircle size={12} style={{ color: '#dc2626' }} /> Nein
                        </>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Dark Mode</span>
                    <p>{selectedUser.data?.isDarkMode ? '🌙 An' : '☀️ Aus'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Tagesziel</span>
                    <p>{selectedUser.data?.dailyGoal ?? 10} Fragen/Tag</p>
                  </div>
                </div>
              </div>

              <hr
                style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' }}
              />

              {/* Progress */}
              <div>
                <span
                  className="text-xs text-muted"
                  style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}
                >
                  📊 Lernfortschritt
                </span>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6,
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                >
                  <div>
                    <span className="text-xs text-muted">Beantwortet</span>
                    <p style={{ fontWeight: 600 }}>
                      {selectedUser.data?.completedQuestions?.length ?? 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Richtig</span>
                    <p style={{ fontWeight: 600, color: '#16a34a' }}>
                      {selectedUser.data?.correctlyAnswered?.length ?? 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Fehler</span>
                    <p style={{ fontWeight: 600, color: '#dc2626' }}>
                      {selectedUser.data?.failedQuestions?.length ?? 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted">Lesezeichen</span>
                    <p style={{ fontWeight: 600 }}>
                      {selectedUser.data?.bookmarkedQuestions?.length ?? 0}
                    </p>
                  </div>
                </div>

                {/* Chapter Progress Bars */}
                {selectedUser.data?.chapterProgress &&
                  Object.keys(selectedUser.data.chapterProgress).length > 0 && (
                    <div>
                      <span
                        className="text-xs text-muted"
                        style={{ display: 'block', marginBottom: 4 }}
                      >
                        Kapitel-Fortschritt
                      </span>
                      {Object.entries(selectedUser.data.chapterProgress)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([chId, pct]) => (
                          <div
                            key={chId}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              marginBottom: 3,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                width: 28,
                                textAlign: 'right',
                                color: 'var(--text-muted)',
                              }}
                            >
                              K{chId}
                            </span>
                            <div
                              style={{
                                flex: 1,
                                height: 6,
                                background: 'var(--border)',
                                borderRadius: 3,
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: `${Math.min(pct, 100)}%`,
                                  height: '100%',
                                  background:
                                    pct >= 100
                                      ? '#16a34a'
                                      : pct >= 50
                                        ? '#ca8a04'
                                        : 'var(--primary)',
                                  borderRadius: 3,
                                  transition: 'width 0.3s',
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: 10,
                                width: 30,
                                color: pct >= 100 ? '#16a34a' : 'var(--text-muted)',
                              }}
                            >
                              {Math.round(pct)}%
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
              </div>

              {/* Badges */}
              {selectedUser.data?.badges && selectedUser.data.badges.length > 0 && (
                <div>
                  <span
                    className="text-xs text-muted"
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    🏅 Badges ({selectedUser.data.badges.length})
                  </span>
                  <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
                    {selectedUser.data.badges.map((b: any, i: number) => (
                      <span
                        key={typeof b === 'string' ? b : b?.id || i}
                        className="badge"
                        style={{
                          fontSize: 10,
                          background: '#fef3c7',
                          color: '#92400e',
                          padding: '2px 6px',
                        }}
                      >
                        {typeof b === 'string'
                          ? b
                          : `${b?.icon || '🏅'} ${b?.name || b?.id || 'Badge'}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <hr
                style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' }}
              />

              {/* Admin Note */}
              <div>
                <span
                  className="text-xs text-muted"
                  style={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <StickyNote size={12} /> Admin-Notiz
                </span>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Interne Notiz zum Nutzer..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  style={{ fontSize: 12, resize: 'vertical', width: '100%' }}
                />
                <button
                  className="btn btn-secondary btn-sm w-full"
                  style={{ marginTop: 4 }}
                  onClick={saveNote}
                  disabled={savingNote || noteText === (selectedUser.data?.adminNote || '')}
                >
                  {savingNote ? (
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Check size={12} />
                  )}
                  Notiz speichern
                </button>
              </div>

              <hr
                style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' }}
              />

              {/* Action Buttons */}
              <button
                className={`btn btn-sm w-full ${selectedUser.data?.isPremium ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => togglePremium(selectedUser)}
              >
                <Crown size={14} />
                {selectedUser.data?.isPremium ? 'Premium entziehen' : 'Premium freischalten'}
              </button>

              <button
                className={`btn btn-sm w-full ${selectedUser.data?.isBlocked ? 'btn-primary' : 'btn-danger'}`}
                onClick={() => toggleBlock(selectedUser)}
              >
                {selectedUser.data?.isBlocked ? (
                  <>
                    <Check size={14} /> Nutzer entsperren
                  </>
                ) : (
                  <>
                    <Ban size={14} /> Nutzer sperren
                  </>
                )}
              </button>

              <button
                className="btn btn-secondary btn-sm w-full"
                onClick={() => handleDsgvoExport(selectedUser)}
              >
                <FileDown size={14} />
                DSGVO-Export (JSON)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreated={() => {
            setShowCreateUser(false);
            loadUsers();
          }}
          setMessage={setMessage}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  function handleDsgvoExport(user: UserRow) {
    const exportData = {
      _meta: { exportDate: new Date().toISOString(), type: 'DSGVO-Datenexport', userId: user.id },
      personalData: {
        email: user.email,
        name: user.data?.name || null,
        language: user.data?.language || 'de',
      },
      accountStatus: {
        isPremium: user.data?.isPremium || false,
        isBlocked: user.data?.isBlocked || false,
        lastLogin: user.data?.lastLoginDate || null,
        lastUpdate: user.updated_at,
        registeredAt: user.created_at,
      },
      gamification: {
        level: user.data?.level || 0,
        xp: user.data?.xp || 0,
        streak: user.data?.streak || 0,
        badges: user.data?.badges || [],
      },
      progress: {
        completedQuestions: user.data?.completedQuestions?.length || 0,
        correctlyAnswered: user.data?.correctlyAnswered?.length || 0,
        failedQuestions: user.data?.failedQuestions?.length || 0,
        chapterProgress: user.data?.chapterProgress || {},
      },
      settings: {
        isDarkMode: user.data?.isDarkMode,
        consentGiven: user.data?.consentGiven,
        shareDataWithAI: user.data?.shareDataWithAI,
        dailyGoal: user.data?.dailyGoal,
      },
      rawData: user.data,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dsgvo_export_${user.email}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setMessage({ text: `DSGVO-Export für ${user.email} heruntergeladen.`, type: 'success' });
  }

  function handleExportUsersCSV() {
    const headers = [
      'E-Mail',
      'Name',
      'Status',
      'Level',
      'XP',
      'Streak',
      'Fragen beantwortet',
      'Sprache',
      'Registriert',
      'Letzte Aktivität',
    ];
    const rows = users.map((u) => [
      u.email,
      u.data?.name || '',
      u.data?.isPremium ? 'Premium' : 'Free',
      u.data?.level || 0,
      u.data?.xp || 0,
      u.data?.streak || 0,
      u.data?.completedQuestions?.length || 0,
      u.data?.language || 'de',
      u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '',
      u.updated_at ? new Date(u.updated_at).toLocaleDateString('de-DE') : '',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahryo_nutzer_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }
};

// ---- Create User Modal ----
const CreateUserModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
  setMessage: (msg: { text: string; type: string }) => void;
}> = ({ onClose, onCreated, setMessage }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [isTestUser, setIsTestUser] = useState(true);
  const [language, setLanguage] = useState('de');
  const [creating, setCreating] = useState(false);

  const generatePassword = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#';
    const pw = Array.from(
      { length: 12 },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join('');
    setPassword(pw);
  };

  const handleCreate = async () => {
    if (!email || !password || password.length < 6) {
      setMessage({ text: 'E-Mail und Passwort (min. 6 Zeichen) erforderlich.', type: 'error' });
      return;
    }
    setCreating(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, isTestUser },
          emailRedirectTo: window.location.origin,
        },
      });
      if (authError) throw authError;

      const userId = authData.user?.id;
      if (userId) {
        const userData = {
          name: name || email.split('@')[0],
          xp: 0,
          level: 1,
          streak: 0,
          isPremium,
          language,
          isTestUser,
          completedQuestions: [],
          chapterProgress: {},
          lastLoginDate: new Date().toISOString(),
        };

        await adminDb.from('users').upsert({ id: userId, email, data: userData });

        await adminDb.from('audit_logs').insert({
          action: 'user_created',
          resource_type: 'users',
          admin_email: 'admin',
          details: { email, name, isPremium, isTestUser },
        });
      }

      setMessage({
        text: `Nutzer ${email} angelegt!${isTestUser ? ' (Testuser)' : ''} Passwort: ${password}`,
        type: 'success',
      });
      onCreated();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 480,
          padding: 32,
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2>
            <UserPlus size={20} /> Nutzer anlegen
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Name
            </label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Max Mustermann"
            />
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              E-Mail *
            </label>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Passwort *
            </label>
            <div className="flex gap-2">
              <input
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 Zeichen"
                style={{ flex: 1 }}
              />
              <button className="btn btn-secondary btn-sm" type="button" onClick={generatePassword}>
                Generieren
              </button>
            </div>
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Sprache
            </label>
            <select
              className="input"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="de">Deutsch</option>
              <option value="tr">Türkisch</option>
              <option value="ar">Arabisch</option>
              <option value="en">Englisch</option>
            </select>
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isPremium}
                onChange={(e) => setIsPremium(e.target.checked)}
              />
              <Crown size={14} style={{ color: 'var(--warning)' }} /> Premium
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isTestUser}
                onChange={(e) => setIsTestUser(e.target.checked)}
              />
              🧪 Test-User
            </label>
          </div>

          {isTestUser && (
            <div className="alert alert-info" style={{ padding: 8, fontSize: 12 }}>
              Test-User werden in der App als solche markiert und können später leicht
              identifiziert/gelöscht werden.
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <button className="btn btn-secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
            {creating ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <UserPlus size={16} />
            )}
            {creating ? 'Erstellt...' : 'Nutzer anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
};
